<template>
    <div v-if="homepageContent" class="flex space-x-2">
        <button @click="modalOpen = true" :class="!homepageContent && 'border border-slate-600 rounded-xl px-3.5 py-3 transition hover:bg-gray-200'">
            <QrIcon :icon="homepageContent ? 'pen' : 'film'" class="w-5"/>
        </button>

        <DeleteHomePageContent v-if="homepageContent" class="ml-auto" :content="homepageContent"/>
    </div>
    <div v-else class="group relative flex gap-x-6 rounded-lg p-4 hover:bg-gray-50">
        <div class="mt-1 flex h-11 w-11 flex-none items-center justify-center rounded-lg bg-gray-50 group-hover:bg-white">
            <QrIcon icon="film" class="h-6 w-6 text-gray-600 group-hover:text-brand" aria-hidden="true" />
        </div>
        <div>
            <button @click="modalOpen = true" class="font-semibold text-gray-900">
                {{ trans('homepage.video') }}
                <span class="absolute inset-0" />
            </button>
            <p class="mt-1 text-gray-600" v-text="trans('homepage.video_desc')"/>
        </div>
    </div>

    <QrModal v-model="modalOpen" :title="trans('homepage.video')">
        <QrForm @submit.prevent="saveOrUpdate" class="space-y-4">
            <QrInput
                v-model="menuButtonName"
                :label="trans('homepage.menu_button_name')" trans="homepage.menu_button_name"
                :errors="validationErrors.menu_button_name"
            />
            <QrInput
                v-model="content.text"
                :label="trans('homepage.youtube-address')" trans="homepage.youtube-address"
                :errors="validationErrors['content.text']"
            />
            <QrToggle v-model="passwordProtected" :label="trans('homepage.protect-with-password')"/>
            <QrInput
                v-if="!qrCode.homepage.is_password_protected && passwordProtected"
                v-model="password"
                :errors="validationErrors.password"
                :label="trans('homepage.password')"
                trans="homepage.password"
            />
        </QrForm>

        <template #buttons>
            <QrButton
                :text="trans('button.save')"
                trans="button.save"
                @click="saveOrUpdate"
                :loading="loading"
                :recently-succeeded="recentlySucceeded"
                :recently-failed="recentlyFailed"
            />
        </template>
    </QrModal>
</template>

<script setup>
import {onMounted, ref, watch} from "vue";
import QrIcon from "@/components/application-ui/elements/QrIcon.vue";
import QrModal from "@/components/application-ui/dialogs/QrModal.vue";
import QrForm from "@/components/application-ui/content/QrForm.vue";
import QrInput from "@/components/application-ui/form/QrInput.vue";
import { trans } from "@/libs/helper.js";
import QrButton from "@/components/application-ui/elements/QrButton.vue";
import {useQrCodeHomepageStore} from "@/store/qrCodeHomepage.js";
import QrToggle from "@/components/application-ui/form/QrToggle.vue";
import { storeToRefs } from "pinia";
import { useStatesStore } from "@/store/states.js";
import {useQrCodesStore} from "@/store/qrCodes.js";
import DeleteHomePageContent from "@/components/qr-code/homepage/DeleteHomePageContent.vue";

const props = defineProps({
    homepageContent: Object,
});

const emit = defineEmits(['close']);

const qrCodeStore = useQrCodesStore();
const homepageStore = useQrCodeHomepageStore();
const statesStore = useStatesStore();

const modalOpen = ref(false);
const password = ref('');
const content = ref({
    text: '',
});
const menuButtonName = ref('');
const passwordProtected = ref(false)
const {
    loading,
    validationErrors,
    recentlyFailed,
    recentlySucceeded
} = storeToRefs(statesStore)
const { qrCode } = storeToRefs(qrCodeStore);

const saveOrUpdate = async () => {
    if (props.homepageContent) {
        await homepageStore.updateContent(props.homepageContent.id, {
            menu_button_name: menuButtonName.value,
            content: content.value,
            is_password_protected: passwordProtected.value,
            password: password.value,
        });
    } else {
        await homepageStore.create({
            type: 'video',
            menu_button_name: menuButtonName.value,
            content: content.value,
            is_password_protected: passwordProtected.value,
            password: password.value,
        });
    }

    if (Object.keys(validationErrors.value).length === 0) {
        if (! props.homepageContent) {
            menuButtonName.value = '';
            content.value.text = '';
            passwordProtected.value = false;
            password.value = '';
        }

        modalOpen.value = false
    }
}

watch(modalOpen, () => {
    if (! modalOpen.value) {
        emit('close');
    }
});

onMounted(() => {
    if (props.homepageContent) {
        menuButtonName.value = props.homepageContent.menu_button_name;
        content.value = props.homepageContent.content;
        passwordProtected.value = props.homepageContent.is_password_protected;
        password.value = props.homepageContent.password;
    }
})
</script>
